import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  BadgeCheck,
  Bell,
  ChevronsUpDown,
  CreditCard,
  LogOut,
  Sparkles,
} from "lucide-react";
import { CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Search } from "lucide-react";

type Section = {
  title: string;
  description?: string;
};

function SectionHeader({ title, description }: Section) {
  return (
    <div className="mb-4">
      <h2 className="text-base font-semibold text-zinc-900">{title}</h2>
      {description && (
        <p className="text-sm text-zinc-400 mt-0.5">{description}</p>
      )}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap items-center gap-3">{children}</div>;
}

function Card({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) {
  return (
    <div className="bg-white border border-base-border rounded-xl p-6 space-y-5">
      <h3 className="text-sm font-medium text-zinc-500 uppercase tracking-wide">
        {title}
      </h3>
      {children}
    </div>
  );
}

export const DesignSystemPage = (): React.JSX.Element => {
  return (
    <div className="p-8 space-y-8 bg-neutral-50 min-h-screen">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900">Design System</h1>
        <p className="text-sm text-zinc-400 mt-1">
          All button and badge variants in one place
        </p>
      </div>

      <section className="space-y-4">
        <SectionHeader
          title="Buttons"
          description="All button variants across sizes and states"
        />

        <div className="grid grid-cols-1 gap-4">
          <Card title="New variants — default size">
            <Row>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_default</span>
                <Button variant="new_default">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_secondary</span>
                <Button variant="new_secondary">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_destructive</span>
                <Button variant="new_destructive">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_outline</span>
                <Button variant="new_outline">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_ghost</span>
                <Button variant="new_ghost">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_link</span>
                <Button variant="new_link">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">operational</span>
                <Button variant="operational">Button</Button>
              </div>
            </Row>
          </Card>

          <Card title="New variants — disabled">
            <Row>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_default</span>
                <Button variant="new_default" disabled>
                  Button
                </Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_secondary</span>
                <Button variant="new_secondary" disabled>
                  Button
                </Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_destructive</span>
                <Button variant="new_destructive" disabled>
                  Button
                </Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_outline</span>
                <Button variant="new_outline" disabled>
                  Button
                </Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_ghost</span>
                <Button variant="new_ghost" disabled>
                  Button
                </Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_link</span>
                <Button variant="new_link" disabled>
                  Button
                </Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">operational</span>
                <Button variant="operational" disabled>
                  Button
                </Button>
              </div>
            </Row>
          </Card>

          <Card title="New variants — sizes">
            <div className="space-y-3">
              <div>
                <span className="text-xs text-zinc-400 block mb-2">sm</span>
                <Row>
                  <Button variant="new_default" size="sm">
                    Button
                  </Button>
                  <Button variant="new_secondary" size="sm">
                    Button
                  </Button>
                  <Button variant="new_destructive" size="sm">
                    Button
                  </Button>
                  <Button variant="new_outline" size="sm">
                    Button
                  </Button>
                  <Button variant="new_ghost" size="sm">
                    Button
                  </Button>
                  <Button variant="operational" size="sm">
                    Button
                  </Button>
                </Row>
              </div>
              <div>
                <span className="text-xs text-zinc-400 block mb-2">
                  default
                </span>
                <Row>
                  <Button variant="new_default" size="default">
                    Button
                  </Button>
                  <Button variant="new_secondary" size="default">
                    Button
                  </Button>
                  <Button variant="new_destructive" size="default">
                    Button
                  </Button>
                  <Button variant="new_outline" size="default">
                    Button
                  </Button>
                  <Button variant="new_ghost" size="default">
                    Button
                  </Button>
                  <Button variant="operational" size="default">
                    Button
                  </Button>
                </Row>
              </div>
              <div>
                <span className="text-xs text-zinc-400 block mb-2">lg</span>
                <Row>
                  <Button variant="new_default" size="lg">
                    Button
                  </Button>
                  <Button variant="new_secondary" size="lg">
                    Button
                  </Button>
                  <Button variant="new_destructive" size="lg">
                    Button
                  </Button>
                  <Button variant="new_outline" size="lg">
                    Button
                  </Button>
                  <Button variant="new_ghost" size="lg">
                    Button
                  </Button>
                  <Button variant="operational" size="lg">
                    Button
                  </Button>
                </Row>
              </div>
            </div>
          </Card>

          <Card title="Base shadcn variants">
            <Row>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">default</span>
                <Button variant="default">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">secondary</span>
                <Button variant="secondary">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">destructive</span>
                <Button variant="destructive">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">outline</span>
                <Button variant="outline">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">ghost</span>
                <Button variant="ghost">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">link</span>
                <Button variant="link">Button</Button>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">icon-text</span>
                <Button variant="icon-text">Button</Button>
              </div>
            </Row>
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeader
          title="Badges"
          description="All badge variants grouped by category"
        />

        <div className="grid grid-cols-1 gap-4">
          <Card title="Base shadcn variants">
            <Row>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">default</span>
                <Badge variant="default">Badge</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">secondary</span>
                <Badge variant="secondary">Badge</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">destructive</span>
                <Badge variant="destructive">Badge</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">outline</span>
                <Badge variant="outline">Badge</Badge>
              </div>
            </Row>
          </Card>

          <Card title="New variants">
            <Row>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_default</span>
                <Badge variant="new_default">Badge</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_secondary</span>
                <Badge variant="new_secondary">Badge</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_outline</span>
                <Badge variant="new_outline">Badge</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">new_destructive</span>
                <Badge variant="new_destructive">Badge</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">verified</span>
                <Badge variant="verified">Verified</Badge>
              </div>
            </Row>
          </Card>

          <Card title="Number variants">
            <Row>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">number_default</span>
                <Badge variant="number_default">8</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">number_secondary</span>
                <Badge variant="number_secondary">8</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">number_outline</span>
                <Badge variant="number_outline">8</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">
                  number_destructive
                </span>
                <Badge variant="number_destructive">8</Badge>
              </div>
            </Row>
          </Card>

          <Card title="Status variants (outline)">
            <Row>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">status_new</span>
                <Badge variant="status_new">New</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">
                  status_in_progress
                </span>
                <Badge variant="status_in_progress">In Progress</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">status_approved</span>
                <Badge variant="status_approved">Approved</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">status_done</span>
                <Badge variant="status_done">Done</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">status_canceled</span>
                <Badge variant="status_canceled">Canceled</Badge>
              </div>
            </Row>
          </Card>

          <Card title="Status variants (filled)">
            <Row>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">filled_status_new</span>
                <Badge variant="filled_status_new">New</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">
                  filled_status_in_progress
                </span>
                <Badge variant="filled_status_in_progress">In Progress</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">
                  filled_status_completed
                </span>
                <Badge variant="filled_status_completed">Completed</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">
                  filled_status_canceled
                </span>
                <Badge variant="filled_status_canceled">Canceled</Badge>
              </div>
            </Row>
          </Card>

          <Card title="Service variants">
            <Row>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">service_gray</span>
                <Badge variant="service_gray">Service</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">service_gray_sm</span>
                <Badge variant="service_gray_sm">Service</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">service_blue</span>
                <Badge variant="service_blue">Service</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">service_black</span>
                <Badge variant="service_black">Service</Badge>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">service_white</span>
                <div className="bg-zinc-100 rounded p-1">
                  <Badge variant="service_white">Service</Badge>
                </div>
              </div>
              <div className="flex flex-col items-start gap-1.5">
                <span className="text-xs text-zinc-400">service_secondary</span>
                <Badge variant="service_secondary">Service</Badge>
              </div>
            </Row>
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeader
          title="Dropdown Menu"
          description="Dropdown with groups, shortcuts, submenu, and disabled item"
        />

        <div className="grid grid-cols-1 gap-4">
          <Card title="Default dropdown">
            <CardContent className="flex flex-row gap-4 px-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="new_outline">Open</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem>
                      Profile
                      <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      Billing
                      <DropdownMenuShortcut>⌘B</DropdownMenuShortcut>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      Settings
                      <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      Keyboard shortcuts
                      <DropdownMenuShortcut>⌘K</DropdownMenuShortcut>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem>Team</DropdownMenuItem>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        Invite users
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem>Email</DropdownMenuItem>
                        <DropdownMenuItem>Message</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem>More...</DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuItem>
                      New Team
                      <DropdownMenuShortcut>⌘+T</DropdownMenuShortcut>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>GitHub</DropdownMenuItem>
                  <DropdownMenuItem>Support</DropdownMenuItem>
                  <DropdownMenuItem disabled>API</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive">
                    Log out
                    <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="new_outline"
                    className="w-56 h-auto py-2 justify-start"
                  >
                    <div className="size-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-sm font-semibold text-zinc-900 leading-none truncate">
                        shadcn
                      </p>
                      <p className="text-xs text-zinc-400 truncate mt-0.5">
                        shadcn@example.com
                      </p>
                    </div>
                    <ChevronsUpDown className="size-4 text-zinc-400 shrink-0 ml-auto" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="start">
                  <DropdownMenuLabel className="p-0 font-normal">
                    <div className="flex items-center gap-3 px-1 py-2">
                      <div className="size-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-zinc-900 leading-none truncate">
                          shadcn
                        </p>
                        <p className="text-xs text-zinc-400 truncate mt-0.5">
                          shadcn@example.com
                        </p>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem>
                      <Sparkles className="text-zinc-500" />
                      Upgrade to Pro
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuItem>
                      <BadgeCheck className="text-zinc-500" />
                      Account
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <CreditCard className="text-zinc-500" />
                      Billing
                    </DropdownMenuItem>
                    <DropdownMenuItem>
                      <Bell className="text-zinc-500" />
                      Notifications
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive">
                    <LogOut />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeader
          title="Input"
          description="All input states and configurations"
        />

        <div className="grid grid-cols-1 gap-4">
          <Card title="States">
            <div className="grid grid-cols-2 gap-6 max-w-xl">
              <Input label="Default" placeholder="Placeholder" />
              <Input label="With value" defaultValue="Input value" />
              <Input label="Disabled" placeholder="Disabled" disabled />
              <Input
                label="Invalid"
                placeholder="Invalid"
                aria-invalid="true"
              />
            </div>
          </Card>

          <Card title="With description">
            <div className="grid grid-cols-2 gap-6 max-w-xl">
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                description="We will never share your email."
              />
              <Input
                label="Invalid with description"
                placeholder="Invalid"
                aria-invalid="true"
                description="This field has an error."
              />
            </div>
          </Card>

          <Card title="With icon">
            <div className="grid grid-cols-2 gap-6 max-w-xl">
              <Input
                label="Search"
                placeholder="Search..."
                icon={<Search className="size-4 text-zinc-400" />}
              />
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                icon={<Mail className="size-4 text-zinc-400" />}
              />
            </div>
          </Card>

          <Card title="Types">
            <div className="grid grid-cols-2 gap-6 max-w-xl">
              <Input label="Text" type="text" placeholder="Text input" />
              <Input label="Password" type="password" placeholder="Password" />
              <Input label="Number" type="number" placeholder="0" />
            </div>
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeader
          title="Textarea"
          description="Textarea uses the same tokens as Input"
        />

        <div className="grid grid-cols-1 gap-4">
          <Card title="States">
            <div className="grid grid-cols-2 gap-6 max-w-3xl">
              <Textarea label="Default" placeholder="Write something..." />
              <Textarea label="With value" defaultValue="Textarea value" />
              <Textarea label="Disabled" placeholder="Disabled" disabled />
              <Textarea
                label="Invalid"
                placeholder="Invalid"
                aria-invalid="true"
              />
            </div>
          </Card>

          <Card title="With description">
            <div className="grid grid-cols-2 gap-6 max-w-3xl">
              <Textarea
                label="Message"
                placeholder="Type your message..."
                description="This will be visible to the guest."
              />
              <Textarea
                label="Invalid with description"
                placeholder="Invalid"
                aria-invalid="true"
                description="This field has an error."
              />
            </div>
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeader
          title="Checkbox"
          description="Active/inactive, label/description combos, focus, disabled, and pressed"
        />

        <div className="grid grid-cols-1 gap-4">
          <Card title="Default">
            <div className="flex flex-col gap-4 max-w-xl">
              <div className="flex items-start gap-3">
                <Checkbox id="checkbox-default" />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="checkbox-default"
                    className="text-sm font-medium leading-none text-black/96"
                  >
                    Label
                  </label>
                  <p className="text-sm text-black/65">
                    Description text goes here.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox id="checkbox-no-description" />
                <label
                  htmlFor="checkbox-no-description"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Label only
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox aria-label="Checkbox without label" />
                <p className="text-sm text-black/65">No label (aria-label)</p>
              </div>
            </div>
          </Card>

          <Card title="Active / Inactive">
            <div className="flex flex-col gap-4 max-w-xl">
              <div className="flex items-start gap-3">
                <Checkbox id="checkbox-inactive" />
                <label
                  htmlFor="checkbox-inactive"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Inactive (unchecked)
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox id="checkbox-active" defaultChecked />
                <label
                  htmlFor="checkbox-active"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Active (checked)
                </label>
              </div>
            </div>
          </Card>

          <Card title="Focus-visible">
            <div className="flex flex-col gap-4 max-w-xl">
              <div className="flex items-start gap-3">
                <Checkbox id="checkbox-focus" autoFocus />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="checkbox-focus"
                    className="text-sm font-medium leading-none text-black/96"
                  >
                    Focused by default
                  </label>
                  <p className="text-sm text-black/65">
                    Uses <span className="font-medium">autoFocus</span> to show
                    focus-visible ring.
                  </p>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Disabled">
            <div className="flex flex-col gap-4 max-w-xl">
              <div className="flex items-start gap-3">
                <Checkbox id="checkbox-disabled" disabled />
                <label
                  htmlFor="checkbox-disabled"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Disabled (unchecked)
                </label>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="checkbox-disabled-checked"
                  disabled
                  defaultChecked
                />
                <label
                  htmlFor="checkbox-disabled-checked"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Disabled (checked)
                </label>
              </div>
            </div>
          </Card>

          <Card title="Pressed">
            <div className="flex flex-col gap-4 max-w-xl">
              <div className="flex items-start gap-3">
                <Checkbox id="checkbox-pressed-demo" />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="checkbox-pressed-demo"
                    className="text-sm font-medium leading-none text-black/96"
                  >
                    Press and hold
                  </label>
                  <p className="text-sm text-black/65">
                    Active styles apply while mouse button is down.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeader
          title="Switch"
          description="Checked/unchecked, focus-visible, disabled, with/without label and description"
        />

        <div className="grid grid-cols-1 gap-4">
          <Card title="Default">
            <div className="flex flex-col gap-5 max-w-xl">
              <div className="flex items-start gap-3">
                <Switch id="switch-default" />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="switch-default"
                    className="text-sm font-medium leading-none text-black/96"
                  >
                    Label
                  </label>
                  <p className="text-sm text-black/65">
                    Description text goes here.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch id="switch-label-only" />
                <label
                  htmlFor="switch-label-only"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Label only
                </label>
              </div>

              <div className="flex items-center gap-3">
                <Switch aria-label="Switch without label" />
                <p className="text-sm text-black/65">No label (aria-label)</p>
              </div>
            </div>
          </Card>

          <Card title="Checked / Unchecked">
            <div className="flex flex-col gap-4 max-w-xl">
              <div className="flex items-center gap-3">
                <Switch id="switch-unchecked" />
                <label
                  htmlFor="switch-unchecked"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Unchecked
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Switch id="switch-checked" defaultChecked />
                <label
                  htmlFor="switch-checked"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Checked
                </label>
              </div>
            </div>
          </Card>

          <Card title="Focus-visible">
            <div className="flex items-start gap-3 max-w-xl">
              <Switch id="switch-focus" autoFocus />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="switch-focus"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Focused by default
                </label>
                <p className="text-sm text-black/65">
                  Uses <span className="font-medium">autoFocus</span> to show
                  focus-visible ring.
                </p>
              </div>
            </div>
          </Card>

          <Card title="Disabled">
            <div className="flex flex-col gap-4 max-w-xl">
              <div className="flex items-center gap-3">
                <Switch id="switch-disabled" disabled />
                <label
                  htmlFor="switch-disabled"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Disabled (unchecked)
                </label>
              </div>
              <div className="flex items-center gap-3">
                <Switch id="switch-disabled-checked" disabled defaultChecked />
                <label
                  htmlFor="switch-disabled-checked"
                  className="text-sm font-medium leading-none text-black/96"
                >
                  Disabled (checked)
                </label>
              </div>
            </div>
          </Card>
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeader
          title="Select"
          description="Default, placeholder/selected, focus-visible, disabled, and invalid"
        />

        <div className="grid grid-cols-1 gap-4">
          <Card title="Default">
            <div className="grid grid-cols-2 gap-6 max-w-3xl">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium leading-none text-black/96">
                  Default
                </label>
                <Select>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="one">Option one</SelectItem>
                      <SelectItem value="two">Option two</SelectItem>
                      <SelectItem value="three">Option three</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-sm leading-[1.25rem] text-black/55">
                  Description text goes here.
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium leading-none text-black/96">
                  Selected
                </label>
                <Select defaultValue="two">
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="one">Option one</SelectItem>
                      <SelectItem value="two">Option two</SelectItem>
                      <SelectItem value="three">Option three</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-sm leading-[1.25rem] text-black/55">
                  Preselected value example.
                </p>
              </div>
            </div>
          </Card>

          <Card title="Focus-visible / Disabled / Invalid">
            <div className="grid grid-cols-3 gap-6 max-w-5xl">
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium leading-none text-black/96">
                  Focus-visible
                </label>
                <Select>
                  <SelectTrigger className="w-full" autoFocus>
                    <SelectValue placeholder="Focused" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="one">Option one</SelectItem>
                      <SelectItem value="two">Option two</SelectItem>
                      <SelectItem value="three">Option three</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-sm leading-[1.25rem] text-black/55">
                  Uses <span className="font-medium">autoFocus</span>.
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium leading-none text-black/96">
                  Disabled
                </label>
                <Select disabled>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Disabled" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="one">Option one</SelectItem>
                      <SelectItem value="two">Option two</SelectItem>
                      <SelectItem value="three">Option three</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-sm leading-[1.25rem] text-black/55">
                  Non-interactive state.
                </p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium leading-none text-black/96">
                  Invalid
                </label>
                <Select>
                  <SelectTrigger className="w-full" aria-invalid="true">
                    <SelectValue placeholder="Invalid" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="one">Option one</SelectItem>
                      <SelectItem value="two">Option two</SelectItem>
                      <SelectItem value="three">Option three</SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-sm leading-[1.25rem] text-black/55">
                  Uses <span className="font-medium">aria-invalid</span>.
                </p>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};
